import React, { useContext, useEffect, useState } from 'react'
import { ComponentConfig, renderContext } from '@puckeditor/core'
import { ColorField } from 'breaktime-theme/src/puck/fields/ColorField'
import {
  Spacing,
  defaultSpacing,
  SpacingGrid,
} from 'breaktime-theme/src/puck/fields/SpacingField'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  TypographyProps,
  FontWeight,
  backgroundStyleField,
  borderStyleField,
  fontFamilyField,
  fontFamilyFallbackField,
  textTransformField,
  fontWeightOptions,
  applyBackgroundStyle,
  applyBorderStyle,
  applyTypography,
} from 'breaktime-theme/src/puck/fields/StyleFields'
import { vote } from 'services/listicle'
import analytics from 'global/AnalyticsData'
import { PuckListicleVoteContext } from 'steps/listicle/PuckListicleVoteContext'
import { Icon, iconField } from 'breaktime-theme/src/puck/fields/IconField'

// Reuse the original listicle vote button + result markup/styles so the block
// looks identical by default; fields layer customization on top.
import 'steps/shared/VoteButtons.scss'
import 'steps/shared/VoteButtonsResult.scss'

export type ListicleVotingProps = {
  upvoteLabel?: string
  downvoteLabel?: string
  upvoteIcon?: string
  downvoteIcon?: string
  iconSize?: string
  background?: BackgroundStyleProps
  border?: BorderStyleProps
  textColor?: string
  bgHoverColor?: string
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  textTransform?: TypographyProps['textTransform']
  fontSize?: string
  fontWeight?: FontWeight
  padding?: Spacing
  upvoteBarColor?: string
  downvoteBarColor?: string
  barTrackColor?: string
  barHeight?: string
}

export const LISTICLE_VOTING_DEFAULT_PROPS: ListicleVotingProps = {
  upvoteLabel: 'Like',
  downvoteLabel: 'Dislike',
  upvoteIcon: 'fa:thumbs-up',
  downvoteIcon: 'fa:thumbs-down',
  iconSize: '18px',
  background: {},
  border: {},
  textColor: '',
  bgHoverColor: '',
  fontSize: '',
  padding: defaultSpacing,
  upvoteBarColor: '',
  downvoteBarColor: '',
  barTrackColor: '',
  barHeight: '',
}

type VoteResults = {
  upvotes: number
  downvotes: number
  selected: 'UP' | 'DOWN'
}

// Shape provided by ListicleStep3 via PuckListicleVoteContext (a plain-JS module).
type ListicleVoteContextValue = {
  listicleId?: string
  itemId?: string
  step?: unknown
  itemTotal?: number
  itemPosition?: number
  selection?: 'UP' | 'DOWN'
  upvotes?: number
  downvotes?: number
  onVote?: (itemId?: string, selection?: 'UP' | 'DOWN') => void
  // TODO: remove once backend supports puck listicle voting
}

const cssVal = (v?: string) =>
  !v || v === '0' ? undefined : /^\d+(\.\d+)?$/.test(v) ? `${v}px` : v

const applyPadding = (p?: Spacing): React.CSSProperties => ({
  paddingTop: cssVal(p?.top),
  paddingRight: cssVal(p?.right),
  paddingBottom: cssVal(p?.bottom),
  paddingLeft: cssVal(p?.left),
})

function pct(value: number, total: number) {
  return Math.round((value / (total || 1)) * 100)
}

function ListicleVotingRender(props: ListicleVotingProps) {
  const {
    upvoteLabel,
    downvoteLabel,
    upvoteIcon,
    downvoteIcon,
    iconSize,
    background,
    border,
    textColor,
    bgHoverColor,
    fontFamily,
    fontFamilyFallback,
    textTransform,
    fontSize,
    fontWeight,
    padding,
    upvoteBarColor,
    downvoteBarColor,
    barTrackColor,
    barHeight,
  } = props

  const puckCtx = useContext(renderContext)
  const isRenderMode = puckCtx.data.content.length > 0
  const voteCtx = useContext(
    PuckListicleVoteContext,
  ) as ListicleVoteContextValue

  const [results, setResults] = useState<VoteResults | undefined>(undefined)
  const [isVoting, setIsVoting] = useState(false)

  // Seed results from any pre-existing vote (fetched by ListicleStep3).
  useEffect(() => {
    if (
      voteCtx.selection &&
      Number.isInteger(voteCtx.upvotes) &&
      Number.isInteger(voteCtx.downvotes)
    ) {
      setResults({
        upvotes: voteCtx.upvotes as number,
        downvotes: voteCtx.downvotes as number,
        selected: voteCtx.selection,
      })
    } else {
      setResults(undefined)
    }
  }, [voteCtx.selection, voteCtx.upvotes, voteCtx.downvotes])

  // Only set inline props the user actually customised, so the .btn defaults
  // show through otherwise (keeps the original look by default).
  const buttonStyle: React.CSSProperties = {
    // center icon + label (icon was misaligned against the text baseline)
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    ...applyBackgroundStyle(background),
    ...applyBorderStyle(border),
    ...applyTypography({ fontFamily, fontFamilyFallback, textTransform }),
    ...(textColor ? { color: textColor } : {}),
    ...(fontSize ? { fontSize } : {}),
    ...(fontWeight ? { fontWeight: Number(fontWeight) || undefined } : {}),
    ...applyPadding(padding),
  }

  const handleVote = (selection: 'UP' | 'DOWN') => {
    if (isVoting || results) return
    setIsVoting(true)

    analytics.stepEvent('step_interaction_listicle_vote', voteCtx.step, {
      result_id: selection,
      result: selection === 'UP' ? upvoteLabel : downvoteLabel,
      context_id: voteCtx.itemId,
      context_total: voteCtx.itemTotal,
      context_position:
        typeof voteCtx.itemPosition === 'number'
          ? voteCtx.itemPosition + 1
          : undefined,
    })

    vote(voteCtx.listicleId, voteCtx.itemId, selection)
      .then(
        (responseData: {
          detail: { upvotes: number; downvotes: number }
        }) => {
          voteCtx.onVote?.(voteCtx.itemId, selection)
          setResults({
            upvotes: responseData.detail.upvotes,
            downvotes: responseData.detail.downvotes,
            selected: selection,
          })
        },
      )
      .catch((e: unknown) => console.warn('listicle vote failed', e))
      .finally(() => setIsVoting(false))
  }

  if (isRenderMode && results) {
    const total = results.upvotes + results.downvotes
    return (
      <VoteResultBars
        upvoteLabel={upvoteLabel}
        downvoteLabel={downvoteLabel}
        upPercent={pct(results.upvotes, total)}
        downPercent={pct(results.downvotes, total)}
        upvoteBarColor={upvoteBarColor}
        downvoteBarColor={downvoteBarColor}
        barTrackColor={barTrackColor}
        barHeight={barHeight}
        selected={results.selected}
      />
    )
  }

  return (
    <VoteButtons
      upvoteLabel={upvoteLabel}
      downvoteLabel={downvoteLabel}
      upvoteIcon={upvoteIcon}
      downvoteIcon={downvoteIcon}
      iconSize={iconSize}
      buttonStyle={buttonStyle}
      bgHoverColor={bgHoverColor}
      disabled={isVoting}
      // editor preview shouldn't dispatch votes
      onVote={isRenderMode ? handleVote : () => {}}
    />
  )
}

function VoteButtons({
  upvoteLabel,
  downvoteLabel,
  upvoteIcon,
  downvoteIcon,
  iconSize,
  buttonStyle,
  bgHoverColor,
  disabled,
  onVote,
}: {
  upvoteLabel?: string
  downvoteLabel?: string
  upvoteIcon?: string
  downvoteIcon?: string
  iconSize?: string
  buttonStyle: React.CSSProperties
  bgHoverColor?: string
  disabled?: boolean
  onVote: (selection: 'UP' | 'DOWN') => void
}) {
  const style = bgHoverColor
    ? ({ ...buttonStyle, ['--plv-hover-bg' as string]: bgHoverColor })
    : buttonStyle
  return (
    <div
      className="vote-buttons puck-listicle-voting"
      // border/background customization belongs to the buttons only — keep the
      // flex wrapper itself clean (the editor was picking up the button border).
      style={{ border: 'none', background: 'none', padding: 0 }}
    >
      {bgHoverColor && (
        <style
          dangerouslySetInnerHTML={{
            __html:
              '.puck-listicle-voting .vote-btn:hover:not(:disabled){background:var(--plv-hover-bg) !important;}',
          }}
        />
      )}
      <button
        type="button"
        className="vote-btn btn btn-primary"
        style={style}
        disabled={disabled}
        onClick={() => onVote('UP')}
        aria-label="Upvote"
      >
        {upvoteIcon && <Icon value={upvoteIcon} size={iconSize || 18} />}
        {upvoteLabel}
      </button>
      <button
        type="button"
        className="vote-btn btn btn-secondary"
        style={style}
        disabled={disabled}
        onClick={() => onVote('DOWN')}
        aria-label="Downvote"
      >
        {downvoteIcon && <Icon value={downvoteIcon} size={iconSize || 18} />}
        {downvoteLabel}
      </button>
    </div>
  )
}

function VoteResultBars({
  upvoteLabel,
  downvoteLabel,
  upPercent,
  downPercent,
  upvoteBarColor,
  downvoteBarColor,
  barTrackColor,
  barHeight,
  selected,
}: {
  upvoteLabel?: string
  downvoteLabel?: string
  upPercent: number
  downPercent: number
  upvoteBarColor?: string
  downvoteBarColor?: string
  barTrackColor?: string
  barHeight?: string
  selected: 'UP' | 'DOWN'
}) {
  const trackStyle: React.CSSProperties = {
    ...(barTrackColor ? { background: barTrackColor } : {}),
    ...(barHeight ? { height: cssVal(barHeight) } : {}),
  }
  const row = (
    label: string | undefined,
    percent: number,
    barColor: string | undefined,
    isSelected: boolean,
  ) => (
    <div className={`item${isSelected ? ' selected' : ''}`}>
      <div className="meter-and-value">
        <div className="meter" style={trackStyle}>
          <span
            style={{
              width: `${percent}%`,
              ...(barColor ? { backgroundColor: barColor } : {}),
            }}
          />
        </div>
        <div className="label">{percent}%</div>
      </div>
      <div
        className="vote-result-text"
        dangerouslySetInnerHTML={{ __html: label || '' }}
      />
    </div>
  )
  return (
    <div className="results-graph">
      <div className="results-group">
        {row(upvoteLabel, upPercent, upvoteBarColor, selected === 'UP')}
        {row(downvoteLabel, downPercent, downvoteBarColor, selected === 'DOWN')}
      </div>
    </div>
  )
}

const colorField = (label: string) => ({
  type: 'custom' as const,
  label,
  render: (props: React.ComponentProps<typeof ColorField>) => (
    <ColorField {...props} clearable />
  ),
})

export const PuckListicleVoting: ComponentConfig<ListicleVotingProps> = {
  label: 'Listicle Voting',
  defaultProps: LISTICLE_VOTING_DEFAULT_PROPS,
  fields: {
    upvoteLabel: { type: 'text', label: 'Upvote Label' },
    downvoteLabel: { type: 'text', label: 'Downvote Label' },
    upvoteIcon: { ...iconField, label: 'Upvote Icon' },
    downvoteIcon: { ...iconField, label: 'Downvote Icon' },
    iconSize: { type: 'text', label: 'Icon Size', placeholder: '18px' },
    background: { ...backgroundStyleField, label: 'Button Background' },
    border: { ...borderStyleField, label: 'Button Border' },
    textColor: colorField('Button Text Color'),
    bgHoverColor: colorField('Button Hover Background'),
    fontFamily: fontFamilyField,
    fontFamilyFallback: fontFamilyFallbackField,
    textTransform: textTransformField,
    fontSize: { type: 'text', label: 'Font Size', placeholder: 'e.g. 16px' },
    fontWeight: {
      type: 'select',
      label: 'Font Weight',
      options: fontWeightOptions,
    },
    padding: {
      type: 'custom',
      label: 'Button Padding',
      render: ({
        value,
        onChange,
      }: {
        value?: Spacing
        onChange: (v: Spacing) => void
      }) => (
        <SpacingGrid
          title="Padding"
          value={typeof value === 'object' && value ? value : defaultSpacing}
          onChange={onChange}
        />
      ),
    },
    upvoteBarColor: colorField('Upvote Bar Color'),
    downvoteBarColor: colorField('Downvote Bar Color'),
    barTrackColor: colorField('Bar Track Color'),
    barHeight: { type: 'text', label: 'Bar Height', placeholder: '14px' },
  },
  render: props => <ListicleVotingRender {...props} />,
}
